/**
 * 데일리 브리핑 대시보드 (/dashboard) — 앱 첫 화면
 * 오늘의 자산 요약 + 환율 + 최근 흐름 + AI 한줄평
 */
import { Card, Col, Row, Segmented, Spin, Statistic, Typography, Button } from "antd";
import ReactECharts from "src/lib/EChart";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ReloadOutlined, RobotOutlined } from "@ant-design/icons";
import { briefApi } from "src/api/brief";
import { stocksApi } from "src/api/stocks";
import { currency, formatWon } from "src/lib/format";
import { USERS, useDefaultUser } from "src/lib/users";
import { useScreenStore } from "src/stores/screenStore";
import { useThemeStore } from "src/stores/ThemeStore";
import { registerChartTheme, useChartTheme } from "src/components/charts/chartTheme";
import QueryError from "src/components/QueryError";
import FundManagerWidget from "src/features/dashboard/FundManagerWidget";
import GlmQuotaWidget from "src/features/dashboard/GlmQuotaWidget";
import { displayStockName } from "src/lib/stockDisplay";

dayjs.locale("ko");
registerChartTheme();

/** AI 데일리 브리핑 카드 — 브리핑 텍스트 전용 쿼리 (summary_only 카드와 분리, 상호 블로킹 없음) */
const AiBriefingCard = ({ userIds }: { userIds: string[] }) => {
  const queryClient = useQueryClient();
  const isDark = useThemeStore((s) => s.isDarkMode);
  const [refreshing, setRefreshing] = useState(false);

  const q = useQuery({
    queryKey: ["brief", "text", userIds],
    queryFn: () => briefApi.getDailyBriefing(userIds),
    staleTime: 10 * 60_000,
  });

  const refresh = async () => {
    setRefreshing(true);
    try {
      await briefApi.refreshDailyBriefing(userIds);
      await queryClient.invalidateQueries({ queryKey: ["brief", "text"] });
      await queryClient.invalidateQueries({ queryKey: ["brief", "summary"] });
    } finally {
      setRefreshing(false);
    }
  };

  const mdBg = isDark ? "#1a1a1a" : "#fafafa";
  const loading = q.isLoading || refreshing;

  return (
    <Card
      size="small"
      title={
        <span>
          <RobotOutlined style={{ color: "#1677ff", marginRight: 6 }} />
          AI 오늘의 브리핑
        </span>
      }
      extra={
        <Button size="small" icon={<ReloadOutlined />} onClick={refresh} loading={refreshing}>
          재생성
        </Button>
      }
    >
      <Spin spinning={loading && !q.data?.briefing} tip="AI가 브리핑 작성 중...">
        <div className="finance-analysis-markdown" style={{ background: mdBg, borderRadius: 8, padding: "10px 14px", minHeight: 60 }}>
          {q.data?.briefing ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{q.data.briefing}</ReactMarkdown>
          ) : !loading ? (
            <span style={{ color: "#888" }}>브리핑이 없습니다.</span>
          ) : null}
        </div>
      </Spin>
      {q.data?.generated_at && !refreshing && (
        <div style={{ marginTop: 6, fontSize: 11, color: "#999", textAlign: "right" }}>
          생성: {dayjs(q.data.generated_at).format("MM.DD HH:mm")}
          {q.data.from_cache ? " (캐시)" : ""}
        </div>
      )}
    </Card>
  );
};

const DashboardPage = () => {
  const isDark = useThemeStore((s) => s.isDarkMode);
  const chartTheme = useChartTheme();
  const screens = useScreenStore((s) => s.screens);
  const defaultUser = useDefaultUser();
  const [userIds, setUserIds] = useState<string[]>([defaultUser.id]);

  // 자산 요약 (30일 — summary는 기간 말일 기준이라 요약카드 값은 기간과 무관)
  const statusQ = useQuery({
    queryKey: ["brief", "status", userIds],
    queryFn: () => briefApi.getDailyAssetStatus(userIds, 30),
    staleTime: 10 * 60_000,
  });

  // 환율
  const fxQ = useQuery({
    queryKey: ["brief", "fx"],
    queryFn: () => briefApi.getExchangeRate(),
    staleTime: 10 * 60_000,
  });

  // AI 브리핑 summary — 요약카드·주요종목 데이터 (summary_only: LLM 대기 없이 즉시 응답)
  const briefQ = useQuery({
    queryKey: ["brief", "summary", userIds],
    queryFn: () => briefApi.getDailyBriefing(userIds, true),
    staleTime: 10 * 60_000,
  });

  const textColor = isDark ? "#ccc" : "#333";
  const gridColor = isDark ? "#333" : "#eee";

  // 요약
  const summary = statusQ.data?.summary;
  const totalValue = summary?.total?.total_current_value ?? 0;
  const totalInvest = summary?.total?.total_investment ?? 0;
  const totalProfit = totalValue - totalInvest;
  const totalRate = totalInvest > 0 ? (totalProfit / totalInvest) * 100 : 0;

  // 전일 대비 (주식) — briefing summary 기준 (종목 페이지/AI 브리핑과 동일 로직)
  const briefSummary = briefQ.data?.summary;
  const dayChange = briefSummary?.stock?.change ?? 0;
  const dayChangePct = briefSummary?.stock?.change_percent ?? 0;
  const totalDayChange = briefSummary?.total?.day_change ?? 0;

  // 배당·목표수익률 (브리핑 summary v2 — CMA+위탁 가용 계좌 기준)
  const dividend = briefSummary?.dividend ?? null;
  const targetReturn = briefSummary?.target_return ?? null;

  const daily = statusQ.data?.daily || [];

  // 자산 흐름 차트 (전체 current_value)
  const trendOption = useMemo(() => {
    if (daily.length === 0) return null;
    const values = daily.map((d) => (d.stock?.current_value || 0) + (d.fund?.current_value || 0) + (d.fixed_income?.current_value || 0));
    // y축 변동 폭에 따라 라벨 소수점 자릿수 적응 (범위가 좁으면 0.05억 간격 라벨 중복 방지)
    const rangeEok = (Math.max(...values) - Math.min(...values)) / 1e8;
    const yDecimals = rangeEok < 0.5 ? 2 : 1;
    return {
      tooltip: {
        trigger: "axis" as const,
        valueFormatter: (v: number) => currency(v),
      },
      grid: { top: 20, left: 60, right: 15, bottom: 24 },
      xAxis: {
        type: "category" as const,
        data: daily.map((d) => d.date.slice(5)),
        axisLabel: { color: textColor, fontSize: 10, hideOverlap: true },
        axisLine: { lineStyle: { color: gridColor } },
      },
      yAxis: {
        type: "value" as const,
        scale: true, // 0원 기준 제거 — 데이터 등락 폭 기준 스케일
        axisLabel: { color: textColor, fontSize: 10, formatter: (v: number) => `${(v / 1e8).toFixed(yDecimals)}억` },
        splitLine: { lineStyle: { color: gridColor } },
      },
      series: [
        {
          name: "평가자산",
          type: "line" as const,
          smooth: true,
          symbol: "circle",
          symbolSize: 4,
          data: values,
          lineStyle: { width: 2 },
          areaStyle: { opacity: 0.08 },
        },
      ],
    };
  }, [daily, isDark]);

  // 오늘의 주요 종목 — briefing summary의 급등/급락 TOP5 (compare 기반, 전일 종가 대비)
  const movers = useMemo(() => {
    const s = briefQ.data?.summary;
    if (!s) return [] as { id: string; name: string; pct: number; pnl: number }[];
    const gainers = (s.top_gainers || []).map((g) => ({ id: g.stock_id, name: g.stock_name, pct: g.change_percent_krw, pnl: g.profit_krw }));
    const losers = (s.top_losers || []).map((l) => ({ id: l.stock_id, name: l.stock_name, pct: l.change_percent_krw, pnl: l.profit_krw }));
    // 상승 TOP5 + 하락 TOP5 (BE briefing summary와 동일 — top_gainers/losers [:5])
    return [...gainers.slice(0, 5), ...losers.slice(0, 5)];
  }, [briefQ.data]);

  const fx = fxQ.data;

  return (
    <div className="app-main">
      {/* 헤더: 날짜 + 사용자 필터 */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          ☀️ {dayjs().format("M월 D일 dddd")} 브리핑
        </Typography.Title>
        <Segmented
          size="small"
          value={userIds.length === 2 ? "all" : userIds[0]}
          onChange={(v) => setUserIds(v === "all" ? USERS.map((u) => u.id) : [v as string])}
          options={[
            { label: "봉주", value: USERS[0].id },
            { label: "미정", value: USERS[1].id },
            { label: "전체", value: "all" },
          ]}
        />
      </div>

      {/* 자산 요약 카드 — 핵심 소스 쿼리 실패 시 재시도 UI (빈 화면 방지) */}
      {(statusQ.isError || briefQ.isError) && (
        <Card size="small" style={{ marginBottom: 8 }}>
          <QueryError
            compact
            error={(statusQ.error || briefQ.error) as any}
            onRetry={() => {
              if (statusQ.isError) statusQ.refetch();
              if (briefQ.isError) briefQ.refetch();
            }}
          />
        </Card>
      )}
      <Row gutter={[8, 8]}>
        <Col xs={12} md={6}>
          <Card size="small" style={{ borderTop: "2px solid #6366f1" }}>
            <Spin spinning={statusQ.isLoading}>
              <Statistic title="총 평가자산" value={formatWon(totalValue)} formatter={(v) => <span style={{ fontSize: 17, fontWeight: 800 }}>{v as string}</span>} />
              <div style={{ fontSize: 11, color: "#999" }}>{currency(totalValue)}</div>
            </Spin>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" style={{ borderTop: "2px solid #27ae60" }}>
            <Statistic title="투자손익" value={formatWon(totalProfit)} formatter={(v) => <span style={{ fontSize: 17, fontWeight: 800, color: totalProfit >= 0 ? "var(--fred)" : "var(--fblue)" }}>{v as string}</span>} />
            <div style={{ fontSize: 11, color: totalProfit >= 0 ? "var(--fred)" : "var(--fblue)" }}>{totalRate >= 0 ? "+" : ""}{totalRate.toFixed(2)}%</div>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" style={{ borderTop: "2px solid #f59e0b" }}>
            <Spin spinning={briefQ.isLoading}>
              <Statistic title="전일 대비 (주식)" value={`${dayChange >= 0 ? "+" : ""}${formatWon(dayChange)}`} formatter={(v) => <span style={{ fontSize: 17, fontWeight: 800, color: dayChange >= 0 ? "var(--fred)" : "var(--fblue)" }}>{v as string}</span>} />
              <div style={{ fontSize: 11, color: dayChangePct >= 0 ? "var(--fred)" : "var(--fblue)" }}>
                {dayChangePct >= 0 ? "+" : ""}{dayChangePct.toFixed(2)}%{totalDayChange !== 0 && <span style={{ color: "#999" }}> · 전체자산 {totalDayChange >= 0 ? "+" : ""}{formatWon(totalDayChange)}</span>}
              </div>
            </Spin>
          </Card>
        </Col>
        <Col xs={12} md={4}>
          <Card size="small">
            <Statistic title="환율 (USD/KRW)" value={fx ? `₩${fx.current_rate.toFixed(1)}` : "-"} formatter={(v) => <span style={{ fontSize: 17, fontWeight: 800 }}>{v as string}</span>} />
            {fx && (
              <div style={{ fontSize: 11, color: fx.change >= 0 ? "var(--fred)" : "var(--fblue)" }}>
                {fx.change >= 0 ? "▲" : "▼"} {Math.abs(fx.change).toFixed(1)} ({fx.change_percent >= 0 ? "+" : ""}{fx.change_percent.toFixed(2)}%)
              </div>
            )}
          </Card>
        </Col>
        <Col xs={12} md={4}>
          <Card size="small" style={{ borderTop: "2px solid #9c27b0" }}>
            <Spin spinning={briefQ.isLoading}>
              <Statistic
                title={`💰 ${dividend?.month ?? "-"}월 배당`}
                value={dividend ? formatWon(dividend.month_total_krw) : "-"}
                formatter={(v) => <span style={{ fontSize: 17, fontWeight: 800 }}>{v as string}</span>}
              />
              {dividend && (
                <div style={{ fontSize: 11, color: "#999" }}>
                  확정 {formatWon(dividend.month_actual_krw)} · 연 {formatWon(dividend.annual_total_krw)}
                </div>
              )}
            </Spin>
          </Card>
        </Col>
        <Col xs={12} md={4}>
          <Card size="small" style={{ borderTop: "2px solid #00bcd4" }}>
            <Spin spinning={briefQ.isLoading}>
              {targetReturn ? (
                <>
                  <Statistic
                    title="🎯 연간 목표수익률"
                    value={`${targetReturn.adjusted_return >= 0 ? "+" : ""}${targetReturn.adjusted_return.toFixed(2)}%`}
                    formatter={(v) => (
                      <span style={{ fontSize: 17, fontWeight: 800, color: targetReturn.gap_rate >= 0 ? "var(--fred)" : "var(--fblue)" }}>{v as string}</span>
                    )}
                  />
                  <div style={{ fontSize: 11, color: "#999" }}>
                    목표 {targetReturn.target_rate.toFixed(0)}% · {targetReturn.gap_rate >= 0 ? "달성 ✓" : `-${Math.abs(targetReturn.gap_rate).toFixed(2)}%p`}
                  </div>
                </>
              ) : !briefQ.isLoading ? (
                <Statistic title="🎯 연간 목표수익률" value="미설정" formatter={(v) => <span style={{ fontSize: 15, color: "#999" }}>{v as string}</span>} />
              ) : null}
            </Spin>
          </Card>
        </Col>
      </Row>

      {/* AI 브리핑 */}
      <div style={{ marginTop: 8 }}>
        <AiBriefingCard userIds={userIds} />
      </div>

      {/* GLM 토큰 쿼터 위젯 */}
      <div style={{ marginTop: 8 }}>
        <GlmQuotaWidget />
      </div>

      {/* AI 펀드 매니저 위젯 (Phase 4) */}
      <div style={{ marginTop: 8 }}>
        <FundManagerWidget />
      </div>

      {/* 자산 구성 + 최근 흐름 */}
      <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
        <Col xs={24} md={10}>
          <Card size="small" title="자산 구성" style={{ height: "100%" }}>
            <Spin spinning={statusQ.isLoading}>
              {summary ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 8 }}>
                  {[
                    { label: "주식", v: summary.stock.total_current_value, c: "#6366f1" },
                    { label: "펀드", v: summary.fund.total_current_value, c: "#f59e0b" },
                    { label: "고정수익", v: summary.fixed_income.total_current_value, c: "#27ae60" },
                  ].map((it) => {
                    const pct = totalValue > 0 ? (it.v / totalValue) * 100 : 0;
                    return (
                      <div key={it.label}>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 3 }}>
                          <span>{it.label}</span>
                          <span style={{ color: "#666" }}>{formatWon(it.v)} · {pct.toFixed(1)}%</span>
                        </div>
                        <div style={{ height: 6, borderRadius: 3, background: isDark ? "#333" : "#eee", overflow: "hidden" }}>
                          <div style={{ height: "100%", width: `${pct}%`, background: it.c, borderRadius: 3 }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ height: 120 }} />
              )}
            </Spin>
          </Card>
        </Col>
        <Col xs={24} md={14}>
          <Card size="small" title="최근 30일 평가자산">
            <Spin spinning={statusQ.isLoading}>
              {trendOption ? (
                <ReactECharts option={trendOption} style={{ height: screens.sm ? 200 : 160 }} theme={chartTheme} lazyUpdate />
              ) : (
                <div style={{ height: 160 }} />
              )}
            </Spin>
          </Card>
        </Col>
      </Row>

      {/* 오늘의 주요 종목 등락 */}
      <Card size="small" title="📈 오늘의 주요 종목 (전일 대비)" style={{ marginTop: 8 }}>
        <Spin spinning={briefQ.isLoading}>
          {movers.length > 0 ? (
            <div style={{ display: "grid", gridTemplateColumns: screens.sm ? "repeat(3, 1fr)" : "repeat(2, 1fr)", gap: 8 }}>
              {movers.map((m) => (
                <div key={`${m.id}-${m.pnl}`} style={{ padding: "6px 8px", border: "1px solid var(--app-color-border-secondary)", borderRadius: 6, fontSize: 12, minWidth: 0 }}>
                  <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{displayStockName(m.name, m.id)}</div>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 4, marginTop: 2 }}>
                    <span style={{ color: m.pnl >= 0 ? "var(--fred)" : "var(--fblue)", fontWeight: 600, fontSize: 11 }}>
                      {m.pnl >= 0 ? "+" : ""}{formatWon(Math.round(m.pnl))}
                    </span>
                    <span style={{ color: m.pct >= 0 ? "var(--fred)" : "var(--fblue)", fontSize: 11, flexShrink: 0 }}>
                      {m.pct >= 0 ? "+" : ""}{m.pct.toFixed(2)}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : !briefQ.isLoading ? (
            <Typography.Text type="secondary">데이터 없음</Typography.Text>
          ) : null}
        </Spin>
      </Card>
    </div>
  );
};

export default DashboardPage;
